import { EditorThemeClasses } from 'lexical'
import { ThemeType } from 'styles/GeckoTheme'

/**
 * Lexical editor theme classes that integrate with our styled-components theme
 */
export const lexicalTheme: EditorThemeClasses = {
  // Root editor styling
  root: 'lexical-editor-root',

  // Paragraph styling
  paragraph: 'lexical-paragraph',

  // Text formatting
  text: {
    bold: 'lexical-text-bold',
    italic: 'lexical-text-italic',
    underline: 'lexical-text-underline',
    strikethrough: 'lexical-text-strikethrough',
  },

  // Heading styles
  heading: {
    h1: 'lexical-heading-h1',
    h2: 'lexical-heading-h2',
    h3: 'lexical-heading-h3',
  },

  // List styles
  list: {
    ul: 'lexical-list-ul',
    ol: 'lexical-list-ol',
    listitem: 'lexical-list-item',
  },

  // Link styles
  link: 'lexical-link',

  // Custom node styles
  mention: 'lexical-mention',
  embeddedObject: 'lexical-embedded-object',
  hr: 'lexical-hr',

  // Placeholder text
  placeholder: 'lexical-placeholder',

  // Quote styles
  quote: 'lexical-quote',

  // Code styles
  code: 'lexical-code',
  codeHighlight: {
    default: 'lexical-code-highlight',
  },
}

/**
 * CSS-in-JS styles for Lexical editor
 * These will be applied via styled-components
 */
export const lexicalEditorStyles = ({ theme }: { theme: ThemeType }) => `
  .lexical-editor-root {
    position: relative;
    outline: none;
    font-family: inherit;
    font-size: 0.875rem;
    line-height: 1.5;
    color: ${theme.forecast.defaultText};
    background: transparent;
    border: none;
    min-height: 6rem;
    padding: 0.75rem;
    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;
  }

  .lexical-paragraph {
    margin: 0 0 1rem 0;
    position: relative;
  }

  .lexical-paragraph:last-child {
    margin-bottom: 0;
  }

  .lexical-text-bold {
    font-weight: bold;
  }

  .lexical-text-italic {
    font-style: italic;
  }

  .lexical-text-underline {
    text-decoration: underline;
  }

  .lexical-text-strikethrough {
    text-decoration: line-through;
  }

  .lexical-heading-h1 {
    font-size: 1.5rem;
    font-weight: bold;
    margin: 0 0 1rem 0;
    color: ${theme.forecast.defaultText};
  }

  .lexical-heading-h2 {
    font-size: 1.25rem;
    font-weight: bold;
    margin: 0 0 1rem 0;
    color: ${theme.forecast.defaultText};
  }

  .lexical-heading-h3 {
    font-size: 1.125rem;
    font-weight: bold;
    margin: 0 0 1rem 0;
    color: ${theme.forecast.defaultText};
  }

  .lexical-list-ul {
    margin: 0 0 1rem 0;
    padding-left: 1.5rem;
  }

  .lexical-list-ol {
    margin: 0 0 1rem 0;
    padding-left: 1.5rem;
  }

  .lexical-list-item {
    margin: 0.25rem 0;
  }

  .lexical-link {
    color: ${theme.forecast.primary};
    text-decoration: underline;
    cursor: pointer;
  }

  .lexical-link:hover {
    text-decoration: none;
  }

  .lexical-mention {
    background: ${theme.forecast.primary}20;
    color: ${theme.forecast.primary};
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    text-decoration: none;
    font-weight: 500;
    cursor: pointer;
  }

  .lexical-mention:hover {
    background: ${theme.forecast.primary}30;
  }

  .lexical-embedded-object {
    display: inline-block;
    margin: 0.25rem 0;
    border-radius: 0.25rem;
    overflow: hidden;
    max-width: 100%;
  }

  .lexical-placeholder {
    position: absolute;
    top: 0.75rem;
    left: 0.75rem;
    color: ${theme.forecast.secondaryText};
    pointer-events: none;
    font-style: italic;
  }

  .lexical-quote {
    border-left: 0.25rem solid ${theme.forecast.border};
    padding-left: 1rem;
    margin: 1rem 0;
    font-style: italic;
    color: ${theme.forecast.secondaryText};
  }

  .lexical-code {
    background: ${theme.forecast.cardGroupBackground};
    padding: 0.125rem 0.25rem;
    border-radius: 0.25rem;
    font-family: 'Monaco', 'Courier New', monospace;
    font-size: 0.875em;
  }

  .lexical-code-highlight {
    background: ${theme.forecast.cardGroupBackground};
    padding: 1rem;
    border-radius: 0.5rem;
    font-family: 'Monaco', 'Courier New', monospace;
    overflow-x: auto;
    margin: 1rem 0;
  }

  .lexical-hr {
    border: none;
    border-top: 1px solid ${theme.forecast.border};
    margin: 1rem 0;
    cursor: pointer;
    
    &:hover {
      border-top-color: ${theme.forecast.primary};
    }
  }
`
